import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Card, CardActions, CardHeader, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import type { UploadDocumentType } from '~/components/uploadDocument/AddManuallyDropdown';
import { EllipsisText } from '~/components/utils';

interface IPdfPreviewProps {
  fileId: string;
  // May be undefined when the attachment has no URL on the wire; react-pdf's
  // `Document file` renders nothing in that case (an inert preview), mirroring
  // AttachmentPreviewDialog's contract (AMP-585).
  filePath: string | undefined;
  pageNumber?: number;
  title?: string;
  subtitle: UploadDocumentType;
  onDeleted?: (id: string) => void;
  onPreview?: () => void;
}

const PdfPreviewWithActions: React.FC<IPdfPreviewProps> = ({
  fileId,
  filePath,
  pageNumber = 1,
  subtitle,
  title,
  onDeleted,
  onPreview,
}: IPdfPreviewProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const theme = useTheme();
  const fileName = filePath ? pdfjs.getPdfFilenameFromUrl(filePath) : '';

  const menuOpen = Boolean(anchorEl);
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const downloadPdf = async () => {
    if (!filePath) return;
    const response = await fetch(filePath);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  };

  return (
    <Card
      sx={{
        p: 0,
        boxShadow: 'none',
        border: 'none',
        '.react-pdf__Page__canvas': {
          imageRendering: 'crisp-edges;',
          backgroundColor: '#eee !important;',
          height: '150px !important;',
          width: '-webkit-fill-available !important;',
        },
        '.react-pdf__Page__textContent': {
          cursor: 'pointer;',
          userSelect: 'none;',
        },
        '.react-pdf__Page__textContent span': {
          cursor: 'pointer;',
          userSelects: 'none;',
        },
      }}
    >
      <Document file={filePath}>
        <Page
          pageNumber={pageNumber}
          height={200}
          scale={1}
          renderAnnotationLayer={false}
          onClick={() => {
            onPreview?.();
          }}
        />
      </Document>
      <CardActions
        disableSpacing
        sx={{
          p: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardHeader
          sx={{
            alignItems: 'start',
            p: theme.spacing(2),
            textAlign: 'left',
          }}
          avatar={<PictureAsPdfIcon sx={{ color: theme.palette.error.main }} aria-label="files" />}
          action={
            <>
              <IconButton aria-label="settings" onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    void downloadPdf();
                  }}
                  disableRipple
                >
                  <DownloadIcon sx={{ marginRight: '10px' }} />
                  Download
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onDeleted?.(fileId);
                  }}
                  disableRipple
                >
                  <DeleteOutline sx={{ marginRight: '10px' }} />
                  Delete
                </MenuItem>
              </Menu>
            </>
          }
          title={
            <>
              <EllipsisText>{title ?? fileName}</EllipsisText>
            </>
          }
          subheader={subtitle}
        />
      </CardActions>
    </Card>
  );
};

export default PdfPreviewWithActions;
